import { useEffect, useMemo, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import Lottie from 'lottie-react';
import TopNavigation from 'src/pages/NewLandingPage/components/topNavigation/TopNavigation';
import Header from 'src/components/demo-day/header/Header';
import Footer from 'src/components/footer/footer';
import { ian, pentathlonv, gsf, vcmint, ha, chennaiangels, haf, faadcapital, startupsherpas, xl } from 'src/assets/images';
import startupFlowAnimation from 'src/assets/lottie-animation/htraction-startupflow-anim.json';
import Faq from 'src/pages/NewLandingPage/components/Faq';
import { useGetIdentity } from 'src/hooks';
import Testimonial from 'src/pages/NewLandingPage/components/Testimonial';
import cx from './latestLandingPage.module.scss';

type Investor = {
  name: string;
  type: string;
  geo: string[];
  checks: string;
  stages: string[];
  industries: string[];
  match: number;
  color: string;
  abbr: string;
  logo?: string;
};

const investors: Investor[] = [
  {
    name: 'Pentathlon Ventures',
    type: 'VC',
    geo: ['India'],
    checks: '₹4Cr - ₹8Cr',
    stages: ['Seed', 'Series A'],
    industries: ['SaaS'],
    match: 93,
    color: '#1d4e89',
    abbr: 'PV',
    logo: pentathlonv
  },
  {
    name: 'GSF India',
    type: 'Accelerator / VC',
    geo: ['India'],
    checks: '₹50L - ₹4Cr',
    stages: ['Pre-seed', 'Seed'],
    industries: ['All Sectors'],
    match: 90,
    color: '#1a6b3c',
    abbr: 'GF',
    logo: gsf
  },
  {
    name: 'VCMint',
    type: 'VC',
    geo: ['India'],
    checks: '₹50L - ₹2Cr',
    stages: ['Pre-seed', 'Seed'],
    industries: ['All Sectors'],
    match: 88,
    color: '#7b2d8b',
    abbr: 'VM',
    logo: vcmint
  },
  {
    name: 'Hyderabad Angels',
    type: 'Angel Network',
    geo: ['India'],
    checks: '₹50L - ₹2Cr',
    stages: ['Pre-seed', 'Seed'],
    industries: ['All sectors'],
    match: 95,
    color: '#ff5924',
    abbr: 'HA',
    logo: ha
  },
  {
    name: 'The Chennai Angels',
    type: 'Angel Network',
    geo: ['India'],
    checks: '₹50L - ₹2Cr',
    stages: ['Pre-seed', 'Seed'],
    industries: ['All Sectors'],
    match: 87,
    color: '#c47900',
    abbr: 'CA',
    logo: chennaiangels
  },
  {
    name: 'Hyderabad Angels Fund',
    type: 'VC',
    geo: ['India'],
    checks: '₹1Cr - ₹4Cr',
    stages: ['Seed', 'Series A'],
    industries: ['All sectors'],
    match: 92,
    color: '#e05a2b',
    abbr: 'HF',
    logo: haf
  },
  {
    name: 'Faad Capital',
    type: 'Angel Network',
    geo: ['India'],
    checks: '₹50L - ₹2Cr',
    stages: ['Pre-seed', 'Seed'],
    industries: ['All Sectors'],
    match: 85,
    color: '#2e6b4f',
    abbr: 'FC',
    logo: faadcapital
  },
  {
    name: 'Startup Sherpas',
    type: 'Angel Network',
    geo: ['India'],
    checks: '₹50L - ₹2Cr',
    stages: ['Pre-seed', 'Seed'],
    industries: ['Consumer'],
    match: 83,
    color: '#374151',
    abbr: 'SS',
    logo: startupsherpas 
  },
  {
    name: 'XL Ventures',
    type: 'VC',
    geo: ['Singapore'],
    checks: '₹4Cr - ₹8Cr',
    stages: ['Seed', 'Series A'],
    industries: ['Climate & Sustainability'],
    match: 89,
    color: '#8b1a1a',
    abbr: 'XL',
    logo: xl
  },
  {
    name: 'India Angel Network',
    type: 'Angel Network/ VC',
    geo: ['India'],
    checks: '₹50L - ₹2Cr',
    stages: ['Pre-seed', 'Seed'],
    industries: ['All Sectors'],
    match: 97,
    color: '#c94820',
    abbr: 'IA',
    logo: ian
  }
];

const features = [
  {
    number: '1',
    title: 'Smart Investor Matching',
    copy:
      'Get matched with thesis aligned investor. Your L1 evaluation is done even before you hit apply.',
    tags: ['Thesis aligned', 'L1 evaluated']
  },
  {
    number: '2',
    title: 'Deal Room & Pipeline Tracker',
    copy:
      'Manage your entire fundraise, investor pipeline, documents & communication. Built for founders',
    tags: ['Pipeline', 'Documents', 'Communication']
  },
  {
    number: '3',
    title: 'Warm Introduction Network',
    copy:
      'Leverage India\'s largest curated network of angels, micro VCs, family offices, and accelerators',
    tags: ['Curated network', 'Angels', 'Micro VCs']
  },
  {
    number: '4',
    title: 'Assessment & Scoring',
    copy: 'Our proprietary scoring model analyzes various parameters to generate investor grade score',
    tags: ['Scoring model', 'Investor grade']
  }
];

const LatestLandingPage = () => {
  const navigate = useNavigate();
  const [change, setIsChange] = useState(false);
  const { getIdentities } = useGetIdentity();
  const { authenticatedId } = getIdentities();

  // Redirect logged-in users to dashboard
  useEffect(() => {
    if (authenticatedId) {
      navigate('/dashboard', { replace: true });
    }
  }, [authenticatedId, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => {
      setIsChange(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const filteredInvestors = investors;

  const marqueeList = useMemo(() => [...investors, ...investors], []);

  const getAbbrClass = (abbr: string) => {
    const classMap: Record<string, string> = {
      IA: cx.bgIA,
      PV: cx.bgPV,
      GF: cx.bgGF,
      VM: cx.bgVM,
      HA: cx.bgHA,
      CA: cx.bgCA,
      HF: cx.bgHF,
      FC: cx.bgFC,
      SS: cx.bgSS,
      XL: cx.bgXL
    };

    return classMap[abbr] || cx.bgDefault;
  };

  return (
    <div className={cx.page}>
      {/* <Header /> */}
       {authenticatedId ? (<Header />) : <TopNavigation change={change} />}
      <section className={cx.hero} id="top">

        <div className={cx.heroPill}>
          <span className={cx.pulse} />
          Now Live · World&apos;s First AI-Powered Venture OS
        </div>
        <h1>
          Get your startup <em className={cx.headingKeyword}>Funded</em>
        </h1>
        <p className={cx.heroSub}>
          Raise <em>3x</em> faster. Reach to <em>20+</em> investors in one Go.
        </p>

        <div className={cx.heroBtns}>
          <NavLink to="/sign-up" className={cx.heroBtn}>
            Get Started
          </NavLink>
        </div>

        <div className={cx.trustBlock}>
          <div className={cx.trustLabel}>Trusted by Top Investors and Founders</div>
          <div className={cx.statsRow}>
            <div>
              <div className={cx.statNum}>20+</div>
              <div className={cx.statLbl}>Active Investors</div>
            </div>
            <div>
              <div className={cx.statNum}>500+</div>
              <div className={cx.statLbl}>Startups Onboarded</div>
            </div>
            <div>
              <div className={cx.statNum}>$1M+</div>
              <div className={cx.statLbl}>Funds Closed Successfully</div>
            </div>
          </div>
        </div>
      </section>

      <section className={cx.marqueeSection}>
        <div className={cx.marqueeLabel}>Reach out to investors</div>
        <div className={cx.marqueeWrap}>
          <div className={cx.marqueeTrack}>
            {marqueeList.map((investor, index) => (
              <div className={cx.investorChip} key={`chip-${investor.name}-${index}`}>
                <div className={cx.chipAvatar}>
                  {investor.logo ? (
                    <img src={investor.logo} alt={investor.name} />
                  ) : (
                    <div className={`${cx.chipFallback} ${getAbbrClass(investor.abbr)}`}>{investor.abbr}</div>
                  )}
                </div>
                <span>{investor.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={cx.tableSection} id="investors">
        <div className={cx.sectionInner}>
          <p className={cx.stag}>Investor Network</p>
          <h2 className={cx.sh}>
            Apply to <span className={cx.headingKeyword}>thesis aligned investors</span> and get evaluated
          </h2>
          <p className={cx.sp}>
            No mass outreach. No guesswork. Only precise investor alignment.
          </p>

          <div className={cx.tableWrap}>
            <table>
              <thead>
                <tr>
                  <th>Investor</th>
                  <th>Geography</th>
                  <th>Checks</th>
                  <th>Stages</th>
                  <th>Industries</th>
                  <th className={cx.alignRight}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvestors.map((investor) => {
                  return (
                    <tr key={investor.name}>
                      <td>
                        <div className={cx.invCell}>
                          <div className={cx.invLogo}>
                            {investor.logo ? (
                              <img src={investor.logo} alt={investor.name} />
                            ) : (
                              <div className={`${cx.invFallback} ${getAbbrClass(investor.abbr)}`}>{investor.abbr}</div>
                            )}
                          </div>
                          <div>
                            <div className={cx.invName}>{investor.name}</div>
                            <div className={cx.invType}>{investor.type}</div>
                          </div>
                        </div>
                      </td>
                      <td>{investor.geo.join(', ')}</td>
                      <td>{investor.checks}</td>
                      <td>
                        <div className={cx.tagList}>
                          {investor.stages.slice(0, 2).map((stage) => (
                            <span className={cx.tblTag} key={`${investor.name}-${stage}`}>
                              {stage}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>
                        <div className={cx.tagList}>
                          {investor.industries.slice(0, 2).map((industry) => (
                            <span className={cx.tblTag} key={`${investor.name}-${industry}`}>
                              {industry}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className={cx.alignRight}>
                        <button type="button" className={cx.btnSubmit} onClick={() => navigate('/sign-up')} >
                          Sign Up to Apply
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className={cx.tableSignupCta}>
            <div className={cx.tableSignupText}>
              <span className={cx.lockBadge} aria-hidden>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M7 10V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7V10"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <rect x="4" y="10" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="2" />
                  <circle cx="12" cy="16" r="1.5" fill="currentColor" />
                </svg>
              </span>
              <div className={cx.tableSignupContent}>
                <h3>Investors actively use htraction to review and evaluate startups</h3>
                <p>
                  <NavLink to="/sign-up" className={cx.inlineSignupLink}>
                    Sign-Up
                  </NavLink>{' '}
                  to get matched and evaluated by thesis aligned investors
                </p>
              </div>
            </div>
            <NavLink to="/sign-up" className={cx.ctaSignupButton}>
              Sign up & Apply Now
            </NavLink>
          </div>
        </div>
      </section>

      <section className={cx.featureSection} id="features">
        <div className={cx.sectionInner}>
          <p className={cx.stag}>The Platform</p>
          <h2 className={cx.sh}>
            Your <span className={cx.headingKeyword}>unfair advantage</span> in fundraising.
          </h2>
          <div className={cx.featureGrid}>
            {features.map((feature) => (
              <article className={cx.featureCard} key={feature.title}>
                <p className={cx.fn}>{feature.number}</p>
                <h3>{feature.title}</h3>
                <p>{feature.copy}</p>
                <div className={cx.featureTags}>
                  {feature.tags.map((tag) => (
                    <span key={`${feature.title}-${tag}`}>{tag}</span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={cx.howSection} id="how">
        <div className={cx.sectionInner}>
          <p className={cx.stag}>How It Works</p>
          <h2 className={cx.sh}>
            From signup to <span className={cx.headingKeyword}>funded</span>; fast.
          </h2>
          <div className={cx.howGrid}>
            <div className={cx.steps}>
              {[
                {
                  title: 'Build your startup profile',
                  body: 'Set up in under 10 minutes: company overview, metrics, team, and fundraising target.'
                },
                {
                  title: 'Get your htraction score',
                  body: 'Our algorithm evaluates your growth signals and generates a benchmark score. Know where you stand before you pitch.'
                },
                {
                  title: 'Get matched with investors',
                  body: 'Receive curated & thesis aligned investor matches based on your stage, sector and various parameters set by the investors'
                },
                {
                  title: 'Manage your raise, close your round',
                  body: 'Track conversations, share your data room, follow up intelligently, and close all from your htraction deal room.'
                }
              ].map((step, index, array) => (
                <div className={cx.step} key={step.title}>
                  <div className={cx.scon}>
                    <div className={cx.sdot}>{index + 1}</div>
                    {index !== array.length - 1 && <div className={cx.sline} />}
                  </div>
                  <div className={cx.sbody}>
                    <h3>{step.title}</h3>
                    <p>{step.body}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className={cx.scoreCard}>
              <Lottie animationData={startupFlowAnimation} loop autoplay className={cx.scoreAnimation} />
            </div>
          </div>
        </div>
      </section>

      <div id="testimonials">
        <Testimonial />
      </div>

      <Faq />

      <section className={cx.ctaSection}>
        <div className={cx.sectionInner}>
          <h2>
            Your next <span className={cx.headingKeyword}>funding round</span> starts here.
          </h2>
          <p>Join 1,000+ startups already using htraction to find, pitch, and close the right investors.</p>
          <div className={cx.signupBox}>
            <h3>Create your free account</h3>
            <p>Get your Traction Score in under 10 minutes.</p>
            <NavLink to="/sign-up" className={cx.sfSubmit}>
              Create Account
            </NavLink>
            <div className={cx.sfNote}>Join 1,000+ founders</div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LatestLandingPage;
